"use client";

import { saveAdAttribution } from "@billingnow/rest-api-client";
import { clientAuth } from "@billingnow/utilities/auth-client";
import { useEffect } from "react";

/**
 * Captures ad click ids for server-side conversion tracking (Meta CAPI / GA4).
 *
 * The landing page's pixels set their cookies on the top-level domain
 * (.billingnow.com), so this app can read them even though it runs no pixels
 * itself: _fbp/_fbc (Meta), _ga (GA4 client id), _gcl_aw (Google Ads click id).
 * The landing cookie banner's choice travels the same way (billingnow_consent).
 *
 * Renders nothing. Posts once per user per browser, fire-and-forget - the
 * server merges first-click-wins, so repeats are harmless anyway.
 */

const readCookie = (name: string): string | null => {
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
};

/** _ga looks like "GA1.1.123456789.1700000000" - the client id is the last two segments. */
const readGaClientId = (): string | null => {
  const value = readCookie("_ga");
  if (!value) return null;
  const parts = value.split(".");
  return parts.length >= 4 ? `${parts[parts.length - 2]}.${parts[parts.length - 1]}` : null;
};

/** _gcl_aw looks like "GCL.1700000000.<gclid>" - set by Google's conversion linker. */
const readGclid = (): string | null => {
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get("gclid");
  if (fromUrl) return fromUrl;
  const cookie = readCookie("_gcl_aw");
  const parts = cookie?.split(".");
  return parts && parts.length >= 3 ? parts.slice(2).join(".") : null;
};

/** Meta's _fbc format; when only the fbclid URL param survived, rebuild it per spec. */
const readFbc = (): string | null => {
  const cookie = readCookie("_fbc");
  if (cookie) return cookie;
  const fbclid = new URL(window.location.href).searchParams.get("fbclid");
  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : null;
};

/** The landing banner's saved choice (shared on .billingnow.com). */
const readMarketingConsent = (): boolean | undefined => {
  const cookie = readCookie("billingnow_consent");
  if (!cookie) return undefined;
  try {
    const parsed = JSON.parse(cookie) as { marketing?: boolean };
    return typeof parsed.marketing === "boolean" ? parsed.marketing : undefined;
  } catch {
    return undefined;
  }
};

export function AdAttributionCapture() {
  const { data: session } = clientAuth.useSession();
  const userId = session?.user?.id;

  useEffect(() => {
    if (!userId) return;

    const storageKey = `bn_ad_attribution_sent_${userId}`;
    try {
      if (localStorage.getItem(storageKey)) return;
    } catch {
      // Storage unavailable (private mode) - still send; the server dedupes.
    }

    const payload = {
      fbp: readCookie("_fbp") ?? undefined,
      fbc: readFbc() ?? undefined,
      gclid: readGclid() ?? undefined,
      gaClientId: readGaClientId() ?? undefined,
      marketingConsent: readMarketingConsent(),
    };

    void saveAdAttribution(payload)
      .then(() => {
        try {
          localStorage.setItem(storageKey, new Date().toISOString());
        } catch {
          // ignore
        }
      })
      .catch(() => {
        // Fire-and-forget: attribution must never surface errors to the user.
      });
  }, [userId]);

  return null;
}
